import { decodeJWT } from '@/utils/jwtDecoder'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type { User } from '@/types/user'

/**
 * Read a cookie value by name
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null
  }
  
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null
  }
  
  return null
}

/**
 * Get role from user_role cookie
 * Returns the role if found, null otherwise
 */
export function getRoleFromCookie(): string | null {
  const cookieRole = getCookie('user_role')
  if (cookieRole && cookieRole.trim() !== '') {
    const role = cookieRole.trim().toUpperCase()
    console.debug('[RoleHelpers] Role from user_role cookie:', role)
    return role
  }
  return null
}

/**
 * Extract role from a JWT token string
 */
export function extractRoleFromToken(token: string | null): string | null {
  if (!token) {
    return null
  }
  
  try {
    const decoded = decodeJWT(token)
    if (decoded && decoded.role) {
      return decoded.role.toString().toUpperCase()
    }
  } catch (err) {
    console.warn('[RoleHelpers] Failed to decode token to extract role:', err)
  }
  
  return null
}

/**
 * Get role from stored token in localStorage
 */
export function getRoleFromStoredToken(): string | null {
  const token = localStorage.getItem('token')
  return extractRoleFromToken(token)
}

/**
 * Validate and store a token if it's valid
 * Returns the extracted role if token is valid
 */
export function validateAndStoreToken(
  token: string,
  tokenType: 'access' | 'refresh' = 'access'
): { role: string | null; stored: boolean } {
  try {
    const decoded = decodeJWT(token)
    if (!decoded || !decoded.exp) {
      console.warn(`[RoleHelpers] Invalid ${tokenType} token format (missing exp)`)
      return { role: null, stored: false }
    }
    
    // Extract role first (before checking id)
    const role = decoded.role ? decoded.role.toString().toUpperCase() : null
    
    // Check if token is expired
    const isExpired = decoded.exp * 1000 < Date.now()
    if (isExpired) {
      console.warn(`[RoleHelpers] ${tokenType} token is expired`)
      return { role, stored: false }
    }
    
    // Backend REQUIRES token to have 'id' field
    const hasId = decoded.id !== undefined && decoded.id !== null
    if (!hasId) {
      console.warn(`[RoleHelpers] ${tokenType} token missing required id field - NOT storing, but role extracted:`, role)
      return { role, stored: false }
    }
    
    // Token is valid - store it
    const storageKey = tokenType === 'access' ? 'token' : 'refresh_token'
    localStorage.setItem(storageKey, token)
    console.debug(`[RoleHelpers] ${tokenType} token stored successfully with id:`, decoded.id, 'role:', role)
    return { role, stored: true }
  } catch (err) {
    console.warn(`[RoleHelpers] Failed to validate ${tokenType} token:`, err)
    return { role: null, stored: false }
  }
}

/**
 * Refresh JWT token from cookies and extract role
 * Returns the role if successful, null otherwise
 */
export async function refreshTokenAndGetRole(): Promise<string | null> {
  try {
    const refreshResponse = await api.post(API_ROUTES.USER_JWT_REFRESH, {})
    if (refreshResponse.data.jwt_access) {
      const result = validateAndStoreToken(refreshResponse.data.jwt_access, 'access')
      if (result.role) {
        console.debug('[RoleHelpers] Role extracted from refreshed JWT token:', result.role)
        return result.role
      }
    }
  } catch (err) {
    console.warn('[RoleHelpers] Failed to refresh token to get role:', err)
  }
  return null
}

/**
 * Sync role to userStore.currentUser
 */
export function syncRoleToUserStore(
  userStore: { currentUser: User | null },
  role: string | null
): boolean {
  if (!role || !userStore.currentUser) {
    return false
  }
  
  userStore.currentUser = {
    ...userStore.currentUser,
    role: role
  }
  return true
}

/**
 * Get role from multiple sources with priority:
 * Flow: JWT token -> user_role cookie -> empty string
 * 1. Try JWT token from localStorage (or from response if provided)
 * 2. Try user_role cookie (if token not found or invalid)
 * 3. Return empty string (if neither found)
 */
export async function getRoleFromMultipleSources(
  userStore: { currentUser: User | null; fetchCurrentUser: () => Promise<void> },
  tokenFromResponse?: string | null
): Promise<string | null> {
  // Priority 1: Try JWT token from response or localStorage
  const roleFromToken = tokenFromResponse 
    ? extractRoleFromToken(tokenFromResponse)
    : getRoleFromStoredToken()
  
  if (roleFromToken) {
    syncRoleToUserStore(userStore, roleFromToken)
    return roleFromToken
  }
  
  // Priority 2: Try user_role cookie (if token not found or invalid)
  const roleFromCookie = getRoleFromCookie()
  if (roleFromCookie) {
    syncRoleToUserStore(userStore, roleFromCookie)
    return roleFromCookie
  }

  // Priority 3: Try userStore.currentUser.role (already fetched from API)
  if (userStore.currentUser?.role) {
    console.debug('[RoleHelpers] Role from userStore.currentUser:', userStore.currentUser.role)
    return userStore.currentUser.role
  }

  // No role found - return null (will be converted to empty string by caller)
  return null
}

